import { ref } from 'vue';
import type { GoogleMap, GoogleMapLatLngBounds, GoogleMapLatLngLiteral } from '@global-types/googleMap';
import { DefaultViewport } from '@components/StickyTabs/map-constants';

export const useGoogleMap = () => {
  const map = ref<GoogleMap | null>(null);

  const DEFAULT_MAP_CENTER: GoogleMapLatLngLiteral = {
    lat: 48.8566,
    lng: 2.3522,
  };

  const getBoundsFromCoordinates = (
    coordinates: GoogleMapLatLngLiteral[],
  ): GoogleMapLatLngBounds | null => {
    if (!coordinates.length) {
      return null;
    }

    if (typeof google === 'undefined' || !google.maps) {
      return null;
    }

    const googleBounds = new google.maps.LatLngBounds();

    coordinates.forEach((coordinate) => {
      googleBounds.extend({
        lat: coordinate.lat,
        lng: coordinate.lng,
      });
    });

    return googleBounds;
  };

  const getCenterFromBounds = (
    bounds: GoogleMapLatLngBounds | null,
  ): GoogleMapLatLngLiteral => {
    if (!bounds) {
      return DEFAULT_MAP_CENTER;
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    return {
      lat: (ne.lat() + sw.lat()) / 2,
      lng: (ne.lng() + sw.lng()) / 2,
    };
  };

  const zoomIn = (): void => {
    if (!map.value) {
      return;
    }

    map.value.setZoom((map.value.getZoom() ?? 0) + 1);
  };

  const DEFAULT_FRANCE_CENTER: GoogleMapLatLngLiteral = {
    lat: (DefaultViewport.NORTH + DefaultViewport.SOUTH) / 2,
    lng: (DefaultViewport.EAST + DefaultViewport.WEST) / 2,
  };

  const getFranceBounds = (): GoogleMapLatLngBounds | null => {
    if (typeof google === 'undefined' || !google.maps) {
      return null;
    }

    return new google.maps.LatLngBounds(
      new google.maps.LatLng(DefaultViewport.SOUTH, DefaultViewport.WEST),
      new google.maps.LatLng(DefaultViewport.NORTH, DefaultViewport.EAST),
    );
  };

  const zoomOut = (): void => {
    if (!map.value) {
      return;
    }

    map.value.setZoom((map.value.getZoom() ?? 0) - 1);
  };

  const initMap = (newMap: GoogleMap): void => {
    map.value = newMap;

    const transitLayer = new google.maps.TransitLayer();
    transitLayer.setMap(newMap);
  };

  const fitMapToBounds = (bounds: GoogleMapLatLngBounds | null): void => {
    if (!bounds || !map.value) {
      return;
    }

    map.value.fitBounds(bounds);

    if (map.value.getZoom() === 0) {
      map.value.setZoom(6);
    }
  };

  return {
    map,
    zoomIn,
    zoomOut,
    initMap,
    fitMapToBounds,
    getBoundsFromCoordinates,
    getCenterFromBounds,
    getFranceBounds,
    DEFAULT_MAP_CENTER,
    DEFAULT_FRANCE_CENTER,
  };
};
